import { graphHooks } from '@tunasong/graph-lib/react'
import { deepEqual } from '@tunasong/models'
import type { TunaEditor, TunaPlugin } from '@tunasong/plugin-lib'
import type { CoreElement, Entity } from '@tunasong/schemas'
import { isCoreElement, isEntity } from '@tunasong/schemas'
import cn from 'classnames'
import { useCallback, useEffect, useRef } from 'react'
import invariant from 'tiny-invariant'
import { useCreateEditor } from '../hooks/create-editor.js'
import type { ElementEditorProps } from './element-editor.js'
import { ElementEditor } from './element-editor.js'
import styles from './element-editor.module.css'

export interface EntityEditorProps<T extends Entity = Entity>
  extends Omit<ElementEditorProps, 'editor' | 'onChange' | 'element'> {
  className?: string
  element: T
  gutter?: boolean
  plugins: TunaPlugin[]
  onEditor?(editor: TunaEditor): void
}

/** EntityEditor requires a persisted entity and will update the Redux state */

export function EntityEditor<T extends Entity = Entity>(props: EntityEditorProps<T>) {
  const { className, plugins = null, element, gutter = false, readOnly, onEditor, ...restProps } = props

  const editor = useCreateEditor(element, plugins)

  useEffect(() => {
    if (editor && onEditor) {
      onEditor(editor)
    }
  }, [editor, onEditor])

  const update = graphHooks.useEntityUpdate({ debounceDelay: 1 * 1000 })

  /**
   * Last document value we handed to `update`, seeded with what we mounted
   * with. GH #1464: Plate fires `onValueChange` on mount (after the editor's
   * initial normalization) and on every editor re-create, so wiring it
   * straight to `update` PATCHes the entity — bumping `updatedAt` — on every
   * load with no user edit. Legacy notes (children, no `yDoc`) route here via
   * `DynamicEntityEditor`, which is why "notes created in March" showed up as
   * updated on each load of the Performance view. Same family as GH #1280.
   *
   * The ref tracks what we *sent*, not the prop, so an edit-then-undo back to
   * the original value still writes (the intermediate value was persisted).
   */
  const lastWrittenChildren = useRef<unknown>(isCoreElement(element) ? element.children : undefined)

  const handleChange = useCallback(
    async (e: CoreElement) => {
      invariant(isEntity(e) && e.id, `EntityEditor for non-entity: ${JSON.stringify(e)}`)

      if (deepEqual(e.children, lastWrittenChildren.current)) {
        return
      }
      lastWrittenChildren.current = e.children

      update(e.id, e)
    },
    [update]
  )

  if (!isCoreElement(element)) {
    return null
  }

  return editor ? (
    <ElementEditor
      className={cn(className, gutter && !readOnly && styles['editorContainer--gutter'])}
      {...restProps}
      gutter={gutter}
      readOnly={readOnly}
      element={element}
      editor={editor}
      onChange={handleChange}
    />
  ) : null
}

export default EntityEditor
